import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  afterNextRender,
  computed,
  inject,
  output,
  viewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';

import { LanguageService } from '../../../../core/services/language.service';
import { OnboardingPanelTab } from '../../models/onboarding.types';
import { OnboardingStore } from '../../store/onboarding.store';
import { OnboardingChecklistComponent } from '../onboarding-checklist/onboarding-checklist.component';
import { OnboardingCompletionComponent } from '../onboarding-completion/onboarding-completion.component';
import { OnboardingTutorialCatalogComponent } from '../onboarding-tutorial-catalog/onboarding-tutorial-catalog.component';

/**
 * Painel lateral não modal: o usuário continua usando a plataforma com ele aberto. Esc fecha e
 * o foco volta para quem abriu (o shell cuida disso via evento closed).
 */
@Component({
  selector: 'app-onboarding-panel',
  standalone: true,
  imports: [
    MatIconModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    TranslateModule,
    OnboardingChecklistComponent,
    OnboardingCompletionComponent,
    OnboardingTutorialCatalogComponent,
  ],
  templateUrl: './onboarding-panel.component.html',
  styleUrl: './onboarding-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OnboardingPanelComponent {
  protected readonly store = inject(OnboardingStore);
  private readonly language = inject(LanguageService);

  readonly closed = output<void>();

  private readonly heading = viewChild<ElementRef<HTMLElement>>('heading');

  readonly tabs: OnboardingPanelTab[] = ['steps', 'tutorials'];

  readonly progressLabel = computed(() => {
    const state = this.store.state();
    return state ? { done: state.requiredCompleted, total: state.requiredTotal } : { done: 0, total: 0 };
  });

  /** Hora da última verificação no formato do idioma escolhido na plataforma (16:22 / 4:22 PM). */
  readonly checkedAtTime = computed(() => {
    const checkedAt = this.store.state()?.checkedAt;
    if (!checkedAt) {
      return '';
    }
    return new Intl.DateTimeFormat(this.language.currentLocale(), { hour: '2-digit', minute: '2-digit' }).format(new Date(checkedAt));
  });

  constructor() {
    afterNextRender(() => this.heading()?.nativeElement.focus());
  }

  close(): void {
    this.store.closePanel();
    this.closed.emit();
  }

  selectTab(tab: OnboardingPanelTab): void {
    this.store.selectTab(tab);
  }

  /** Setas esquerda/direita alternam as abas (padrão WAI-ARIA de tablist). */
  onTabKeydown(event: KeyboardEvent): void {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') {
      return;
    }
    event.preventDefault();
    const next = this.store.panelTab() === 'steps' ? 'tutorials' : 'steps';
    this.selectTab(next);
    globalThis.document?.getElementById(`onboarding-panel-tab-${next}`)?.focus();
  }
}
